import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout as logoutAction } from './authSlice';
import type { AuthState } from './types';

type RootWithAuth = { auth: AuthState };

/**
 * Background watcher — every 30 seconds, re-probes
 * `/auth/session/{sessionId}` and forces a logout when the server
 * reports the session has been revoked OR the local sessionId no
 * longer matches the one in the response. This catches the case
 * where the user signs in from a different device / browser and the
 * server invalidates the older session.
 *
 * Bug ref: AI Agent 2026-05-14 — "session should be managed by
 * session id, if session is changed it should be log out".
 */
const SESSION_PROBE_INTERVAL_MS = 30_000;

/**
 * Validates the persisted session on app startup against the live API.
 *
 * Flow:
 * 1. Reads `auth.session` from the rehydrated Redux store. Bails if absent.
 * 2. Calls `GET /auth/session/{sessionId}` and inspects `data.active`.
 *    The session response only carries metadata (sessionId, ip, userAgent,
 *    issuedAt, refreshExpiresAt, isRevoked) — not user profile fields, so
 *    there is nothing to sync back into the store from this call.
 * 3. If the session is revoked, expired, or the request 401s, dispatches
 *    `logout('refresh-failed')` so the SessionEndedBanner surfaces and
 *    `RequireAuth` redirects to `/login`.
 *
 * Runs at most once per mount — guarded by a ref. Mount this once near the
 * root of the app (the app's `<App />` component) after `<Providers />`.
 */
export function useSessionBootstrap() {
  const dispatch = useDispatch();
  const session = useSelector((s: RootWithAuth) => s.auth.session);
  const isAuthenticated = useSelector((s: RootWithAuth) => s.auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated || !session) return;

    const sessionId = session?.sessionId;
    if (!sessionId) return;

    // Local expiry pre-check before hitting the API. Without this, a
    // user who opens the app a day after closing it would see an
    // authenticated UI for a moment, then a 401 cascade. With this
    // check we surface the "session expired" banner immediately.
    //
    // If the access-token expiry is more than 7 days in the past the
    // refresh token is also certainly dead (live API issues 24h
    // refresh tokens). Force a logout-expired so the login modal
    // explains why instead of letting `useFetch` refresh-loops fail.
    const expiresAt = session.expiresAt ? Date.parse(session.expiresAt) : NaN;
    if (Number.isFinite(expiresAt)) {
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - expiresAt > sevenDaysMs) {
        dispatch(logoutAction('expired'));
        return;
      }
    }

    let cancelled = false;

    const probe = async () => {
      try {
        const api = await import('@kss/api');
        const me = await api.authService.session(sessionId);
        if (cancelled) return;
        // Force logout when the server reports the session is gone,
        // OR when the server-side sessionId no longer matches the
        // one we have (catches sign-in-from-another-device flows).
        const serverSessionId = me?.info?.sessionId;
        const mismatch =
          typeof serverSessionId === 'string' &&
          serverSessionId.length > 0 &&
          serverSessionId !== sessionId;
        if (me?.active === false || me?.info?.isRevoked || mismatch) {
          dispatch(logoutAction('refresh-failed'));
        }
      } catch {
        if (cancelled) return;
        dispatch(logoutAction('refresh-failed'));
      }
    };

    // Probe once on mount, then on a recurring interval so a session
    // revoked elsewhere is reflected within ~30s in the active tab.
    probe();
    const id = window.setInterval(probe, SESSION_PROBE_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [dispatch, isAuthenticated, session]);
}
